// Components
import { Stack, Select, Divider } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { PhoneInput } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import {
  InsuranceProviderCombobox,
  type InsuranceSelection,
} from "./InsuranceProviderCombobox";

// Constants
import { getRelationshipToSubscriberOptions } from "@benchmark/core/constants";

// Hooks
import { useCallback } from "react";

// Types
import type { UseFormReturnType } from "@mantine/form";

/**
 * Props for InsuranceFieldsGroup component.
 * @agentic @benchmark/core
 */
type InsuranceFieldsGroupProps = {
  form: UseFormReturnType<Record<string, string | boolean | undefined>>;
  isPrimary?: boolean;
};

/**
 * Form group for primary or secondary insurance (provider, subscriber ID, relationship).
 * @agentic @benchmark/core
 */
export const InsuranceFieldsGroup = ({
  form,
  isPrimary = true,
}: InsuranceFieldsGroupProps) => {
  const prefix = isPrimary ? "primary" : "secondary";
  const selfPayValue = form.values["self-pay"] as boolean | undefined;

  // Build current selection from form values
  const currentSelection: InsuranceSelection | null = (() => {
    const providerId = form.values[`${prefix}-insurance-provider`] as
      string | undefined;
    const providerDisplay = form.values[
      `${prefix}-insurance-provider-display`
    ] as string | undefined;
    const planId = form.values[`${prefix}-insurance-plan`] as
      string | undefined;
    const planDisplay = form.values[`${prefix}-insurance-plan-display`] as
      string | undefined;

    if (!providerId) return null;

    return {
      type: planId ? "plan" : "organization",
      organizationId: providerId,
      organizationName: providerDisplay || providerId,
      planId: planId || undefined,
      planName: planDisplay || undefined,
    };
  })();

  // Handle selection change
  const handleSelectionChange = useCallback(
    (selection: InsuranceSelection | null) => {
      if (selection) {
        form.setFieldValue(
          `${prefix}-insurance-provider`,
          selection.organizationId,
        );
        form.setFieldValue(
          `${prefix}-insurance-provider-display`,
          selection.organizationName,
        );
        form.setFieldValue(`${prefix}-insurance-plan`, selection.planId || "");
        form.setFieldValue(
          `${prefix}-insurance-plan-display`,
          selection.planName || undefined,
        );
      } else {
        form.setFieldValue(`${prefix}-insurance-provider`, "");
        form.setFieldValue(`${prefix}-insurance-provider-display`, undefined);
        form.setFieldValue(`${prefix}-insurance-plan`, "");
        form.setFieldValue(`${prefix}-insurance-plan-display`, undefined);
      }
    },
    [form, prefix],
  );

  const isDisabled = !!selfPayValue;

  // For secondary insurance, check if secondary insurance is enabled
  const hasSecondaryInsurance = form.values["has-secondary-insurance"] as
    boolean | undefined;
  const isRequired = isPrimary ? !selfPayValue : hasSecondaryInsurance === true;

  const relationshipValue = form.values[
    `${prefix}-relationship-to-subscriber`
  ] as string | undefined;
  const showSubscriberInfo = relationshipValue && relationshipValue !== "self";

  // Get form error for the provider field
  const providerError = form.errors[`${prefix}-insurance-provider`] as
    string | boolean | undefined;

  return (
    <Stack gap="md">
      <Divider
        label={isPrimary ? "Primary Insurance" : "Secondary Insurance"}
      />

      <FormGroup>
        <FormField label="Insurance Provider / Plan" required={isRequired}>
          <InsuranceProviderCombobox
            value={currentSelection}
            onChange={handleSelectionChange}
            disabled={isDisabled}
            error={providerError}
            required={isRequired}
            placeholder="Search insurance providers or plans..."
          />
        </FormField>

        <FormField label="Subscriber ID" required={isRequired}>
          <TextInput
            placeholder="Enter subscriber ID"
            disabled={isDisabled}
            autoComplete="off"
            {...form.getInputProps(`${prefix}-subscriber-id`)}
          />
        </FormField>
      </FormGroup>

      <FormField label="Relationship to Subscriber" required={isRequired}>
        <Select
          placeholder="Select relationship"
          data={getRelationshipToSubscriberOptions()}
          searchable
          clearable
          disabled={isDisabled}
          {...form.getInputProps(`${prefix}-relationship-to-subscriber`)}
        />
      </FormField>

      {showSubscriberInfo && (
        <>
          <Divider
            label={`${isPrimary ? "Primary" : "Secondary"} Subscriber Information`}
          />
          <FormGroup>
            <FormField label="Subscriber First Name" required={isRequired}>
              <TextInput
                placeholder="First name"
                disabled={isDisabled}
                autoComplete="off"
                {...form.getInputProps(`${prefix}-subscriber-first-name`)}
              />
            </FormField>
            <FormField label="Subscriber Last Name" required={isRequired}>
              <TextInput
                placeholder="Last name"
                disabled={isDisabled}
                autoComplete="off"
                {...form.getInputProps(`${prefix}-subscriber-last-name`)}
              />
            </FormField>
          </FormGroup>

          <FormGroup>
            <FormField label="Subscriber Date of Birth" required={isRequired}>
              <TextInput
                type="date"
                disabled={isDisabled}
                autoComplete="off"
                {...form.getInputProps(`${prefix}-subscriber-dob`)}
              />
            </FormField>
            <FormField label="Subscriber Phone">
              <PhoneInput
                placeholder="Phone number"
                disabled={isDisabled}
                autoComplete="off"
                {...form.getInputProps(`${prefix}-subscriber-phone`)}
              />
            </FormField>
          </FormGroup>
        </>
      )}
    </Stack>
  );
};
